import { useCallback, useEffect, useState } from 'react'
import {
  SlotItemInterface,
  SlotPayload,
  Regularity,
  ValidationResultInterface,
  ValidationRulesInterface,
  CargoItemInterface,
  PointItemInterface,
} from '../../../../common/types/interfaces'

import {
  slotValidations,
  minUploadFilesCount,
  SLOT_MY_ROUTE,
  USER_ROUTE,
} from '../../../../common/utils/constants'

import { useValidation } from '../../../../common/hooks/useValidation'
import { useInput } from '../../../../common/hooks/useInput'
import { FormStage } from '../../../../common/types/enums'
import { getApi } from '../../../../api/api'
import { useNavigate, useParams } from 'react-router-dom'
import { useFiles } from '../../../../common/hooks/useFiles'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../store/store'
import { v4 } from 'uuid'
import { selectSlotItem } from '../../../../store/slices/slotListSlice'
import { Buffer } from 'buffer'
import { useCommonLoader } from '../../../../common/hooks/useCommonLoader'
import { delayAction } from '../../../../common/utils/functions/delayAction'

const validations: ValidationRulesInterface[] = slotValidations
const initialValue: SlotItemInterface = {
  name: '',
  description: '',
  startTime: new Date(Date.now()),
  endTime: new Date(new Date(Date.now()).setTime(new Date().getTime() + 1000 * 60 * 60)),
  regularity: Regularity.NO,
  cost: undefined,
  points: [],
  views: [],
  contacts: [],
  selected: [],
  intercity: false,
  cargo_units: [],
  transport_types: [],
  equipment_types: [],
  cargo_types: [],
  transport_side_types: [],
  transport_load_capacities: [],
  start_important: false,
  end_important: false,
}

export const useSlotForm = () => {
  const [data, setData] = useState<CargoItemInterface>(initialValue)
  const [formStage, setFormStage] = useState<FormStage>(FormStage.ONE)
  const [validationResult, setValidationResult] = useState<ValidationResultInterface[] | []>([])
  const [disabled1, setDisabled1] = useState<boolean>(true)
  const [disabled2, setDisabled2] = useState<boolean>(true)
  const [disabled, setDisabled] = useState<boolean>(true)
  const [api] = useState(getApi())
  const navigate = useNavigate()
  const [intercity, setIntercity] = useState<boolean>(false)
  const [addresses, setAddresses] = useState<boolean>(false)
  const { inputHandler } = useInput(setData)
  const [isRoute] = useState<boolean>(false)
  const [cargoTypeValid, setCargoTypeValid] = useState<boolean>(true)
  const [transportTypeValid, setTransportTypeValid] = useState<boolean>(true)
  const afterActionRoute = localStorage.getItem('afterActionRoute')
  const { showCommonLoader, hideCommonLoader } = useCommonLoader()

  //edit
  const { id } = useParams()

  const [edit, setEdit] = useState<boolean>(false)
  const [editPointsData, setEditPointsData] = useState<PointItemInterface[]>()

  const item = useSelector((state: RootState) => selectSlotItem(state, Number(id)))

  const { files, setFiles, pairs, setPairs, dataInit, fetchFiles } = useFiles()

  useEffect(() => {
    if (item) {
      setEdit(true)

      const cleanItem: CargoItemInterface = JSON.parse(JSON.stringify(item))
      delete cleanItem.createdAt
      delete cleanItem.updatedAt
      delete cleanItem.userId

      cleanItem &&
        cleanItem.points &&
        cleanItem.points.length > 0 &&
        setEditPointsData([
          ...cleanItem.points.map((attrs) => {
            delete attrs.cityId
            delete attrs.addressId
            delete attrs.createdAt
            delete attrs.updatedAt
            if (attrs.address) attrs.address.isDirty = false
            if (attrs.city) attrs.city.isDirty = false
            if (attrs.address) attrs.address.notValid = false
            if (attrs.city) attrs.city.notValid = false
            attrs.id = `a${v4()}`
            return attrs
          }),
        ])

      if (cleanItem.files && cleanItem.files?.length > 0) {
        fetchFiles(cleanItem.files)
        cleanItem.files = []
      }
      cleanItem.points = []
      cleanItem && setData(cleanItem)
    }
  }, [item, fetchFiles])

  const { validate, checkAndAddValidation, setDirty } = useValidation(
    setValidationResult,
    validationResult,
    validations,
  )

  const getValidationResult = (fieldName: string) => {
    return validationResult.find((el) => el.name === fieldName)
  }

  const formAction = useCallback(async () => {
    try {
      await showCommonLoader()
      await delayAction(1000)

      const {
        points,
        priority,
        archived_user,
        archived_participant,
        deleted_user,
        deleted_participant,
        executorId,
        views,
        contacts,
        selected,
        cargo_units,
        can_see_user,
        can_take_user,
        can_see_partner,
        can_take_partner,
        ...rest
      } = data
      const payload: SlotPayload = {
        ...rest,
        file: dataInit(files),
        points: points
          ? [
              ...points.map(({ id, ...keepAttrs }) => {
                if (keepAttrs.address) delete keepAttrs.address.isDirty
                if (keepAttrs.address) delete keepAttrs.address.notValid
                delete keepAttrs.city.isDirty
                delete keepAttrs.city.notValid
                return keepAttrs
              }),
            ]
          : [],
        cargo_units: cargo_units.map((el) => {
          return {
            width: Number(el.width),
            height: Number(el.height),
            length: Number(el.length),
            weight: Number(el.length),
            count: Number(el.count),
          }
        }),
      }

      if (payload.id) {
        await api.updateSlot(payload)
      } else {
        await api.createSlot(payload)
      }

      await hideCommonLoader()

      if (afterActionRoute) {
        localStorage.removeItem('afterActionRoute')
        navigate(SLOT_MY_ROUTE, { replace: true })
        navigate(afterActionRoute)
        return
      }
      navigate(USER_ROUTE, { replace: true })
      navigate(SLOT_MY_ROUTE)
    } catch (error: any) {
      await hideCommonLoader()
    }
  }, [data, api, files, afterActionRoute, dataInit, navigate, showCommonLoader, hideCommonLoader])

  const toggleIntercity = () => {
    setData((prev) => ({ ...prev, intercity: !prev.intercity }))
    !data.intercity && setAddresses(false)
  }

  const toggleStartImportant = () => {
    setData((prev) => ({ ...prev, start_important: !prev.start_important }))
  }
  const toggleEndImportant = () => {
    setData((prev) => ({ ...prev, end_important: !prev.end_important }))
  }

  useEffect(() => {
    if (!data.start_important) setData((prev) => ({ ...prev, end_important: false }))
  }, [data.start_important])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    for (var item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) {
        checkAndAddValidation(item)
      }
    }
  }, [checkAndAddValidation, data])

  useEffect(() => {
    for (var item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) {
        validate(item, data[item])
      }
    }
  }, [data])

  useEffect(() => {
    setDisabled1(
      validationResult.filter((el) => el.name === 'name' && el.notValid).length > 0 ||
        validationResult.filter((el) => el.name === 'description' && el.notValid).length > 0 ||
        !transportTypeValid ||
        !cargoTypeValid,
    )
  }, [validationResult, cargoTypeValid, transportTypeValid])

  useEffect(() => {
    data.points && setDisabled2(Boolean(data.points.find((el) => !el.address.latitude)))
  }, [data.points])

  useEffect(() => {
    setDisabled(Boolean(files.length < minUploadFilesCount))
  }, [files])

  useEffect(() => {
    !intercity && setAddresses(false)
  }, [intercity])

  useEffect(() => {
    if (data.route) {
      console.log(
        `Route size ${(Buffer.byteLength(data.route, 'utf8') / 1024 / 1024).toFixed(2)}mb`,
      )
    }
  }, [data])

  return {
    inputHandler,
    data,
    formAction,
    getValidationResult,
    disabled,
    setDirty,
    setData,
    formStage,
    setFormStage,
    disabled1,
    disabled2,
    files,
    setFiles,
    pairs,
    setPairs,
    intercity,
    setIntercity,
    addresses,
    setAddresses,
    isRoute,
    edit,
    editPointsData,
    toggleIntercity,
    setCargoTypeValid,
    setTransportTypeValid,
    toggleStartImportant,
    toggleEndImportant,
  }
}
